import { useEffect, useState, useRef } from "react";
import useAuth from "../../Users/hooks/useAuth";
import UploadFile from "./UploadFile";
import ProcessFile from "./ProcessFile";
import DropdownLang from "./DropdownLang";
import DropdownModel from "./DropdownModel";

const BookTitle = ({ book_title, successHandler }) => {
    const getModelsForLanguage = (language) => {
        const modelMap = {
            "Norwegian": ["NbAiLab/nb-whisper-large-distil-turbo-beta", "NbAiLab/nb-whisper-tiny", "NbAiLab/nb-whisper-base", "NbAiLab/nb-whisper-small", "NbAiLab/nb-whisper-medium", "NbAiLab/nb-whisper-large", "medium", "large", "turbo"],
            "English": ["tiny.en", "base.en", "small.en", "medium.en", "large", "turbo"],
            "Portuguese": ["tiny", "base", "small", "medium", "pierreguillou/whisper-medium-portuguese", "large", "turbo"],
            "Russian": ["tiny", "base", "small", "medium", "large", "turbo", "openai/whisper-large-v3-turbo", "antony66/whisper-large-v3-russian"],
            "Spanish": ["tiny", "base", "small", "medium", "clu-ling/whisper-large-v2-spanish-5k-steps", "Berly00/whisper-large-v3-spanish", "Sandiago21/whisper-large-v2-spanish", "large", "turbo"],
            "Vietnamese": ["tiny", "base", "small", "medium", "large", "turbo", "vinai/PhoWhisper-large"],
            "French": ["tiny", "base", "small", "medium", "large", "turbo", "bofenghuang/whisper-large-v3-french"]
        };
        return modelMap[language] || ["tiny", "base", "small", "medium", "large", "turbo"];
    };

    const models = getModelsForLanguage(book_title.language);
    
    const [listFiles, setListFiles] = useState();
    const { auth, setAuth } = useAuth();
    const [updated, setUpdated] = useState(false);
    const [model, setModel] = useState(models[2]);
    const [transcript, setTranscript] = useState();
    const [segment, setSegment] = useState();
    const [audioSnap, setAudioSnap] = useState();

    // Audio state management
    const [currentAudio, setCurrentAudio] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentSegment, setCurrentSegment] = useState(null);
    const audioRef = useRef(null);

    // Sorting state for audio files
    const [sortBy, setSortBy] = useState("date"); // "name" or "date"
    const [sortOrder, setSortOrder] = useState("desc"); // "asc" or "desc"

    const fetchData = async (route, method) => {
        const token = localStorage.getItem("token");
        const url = `${process.env.REACT_APP_USER_API_URL}/upload/${route}`;
        const response = await fetch(url, {
            method: method,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            }
        });
        if (response.status === 401) {
            localStorage.removeItem("token");
            setAuth({});
            return null;
        }
        if (response.ok) {
            const json = await response.json();
            return json;
        }
        return null;
    };

    const getFiles = async (title) => {
        const json = await fetchData(`list_files/${title.language}/${title.author}/${title.title}/${title.type}`, "GET");
        if (json) {
            setListFiles(json);
            setUpdated(false);
        }
    };

    const getAudioSnap = async ({ author, file, title, language, start, end, type }) => {
        const token = localStorage.getItem("token");
        const outdata = {
            author: author,
            file: file,
            start: start,
            end: end,
            title: title,
            language: language,
            type: type
        };
        
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/audio/get-snap`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(outdata),
        });

        if (response.ok) {
            const json = await response.json();
            return json;
        }
        return null;
    };

    // Play/pause functionality
    const playAudioSegment = async (segmentData) => {
        // Stop current audio if playing
        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
        }

        // If clicking the same segment that's currently playing, toggle pause/play
        if (currentSegment === segmentData && currentAudio) {
            if (isPlaying) {
                currentAudio.pause();
                setIsPlaying(false);
            } else {
                await currentAudio.play();
                setIsPlaying(true);
            }
            return;
        }

        // Get new audio segment
        const audioUrl = await getAudioSnap({
            start: segmentData.start,
            end: segmentData.end,
            author: transcript.author,
            language: transcript.language,
            title: transcript.title,
            file: transcript.file,
            type: transcript.type
        });

        if (audioUrl) {
            const audio = new Audio(`${process.env.REACT_APP_USER_API_URL}` + audioUrl);
            
            audio.addEventListener('loadeddata', () => {
                audio.play().then(() => {
                    setIsPlaying(true);
                    setCurrentAudio(audio);
                    setCurrentSegment(segmentData);
                }).catch(error => {
                    console.error("Error playing audio:", error);
                    setIsPlaying(false);
                });
            });

            audio.addEventListener('ended', () => {
                setIsPlaying(false);
                setCurrentSegment(null);
            });

            audio.addEventListener('error', () => {
                setIsPlaying(false);
                setCurrentSegment(null);
            });
        }
    };

    // Stop all audio
    const stopAllAudio = () => {
        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            setIsPlaying(false);
            setCurrentAudio(null);
            setCurrentSegment(null);
        }
    };

    // Close transcript handler
    const handleCloseTranscript = () => {
        stopAllAudio();
        setTranscript(null);
    };

    // Sorting function for audio files
    const sortFiles = (files) => {
        if (!files || !files.files) return files;
        
        const sortedFiles = [...files.files].sort((a, b) => {
            let aValue, bValue;
            
            if (sortBy === "name") {
                aValue = a.toLowerCase();
                bValue = b.toLowerCase();
                
                if (aValue < bValue) {
                    return sortOrder === "asc" ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortOrder === "asc" ? 1 : -1;
                }
                return 0;
            } else { // date sorting - use array index as proxy for upload order
                const originalIndexA = files.files.indexOf(a);
                const originalIndexB = files.files.indexOf(b);
                
                return sortOrder === "desc" ? originalIndexB - originalIndexA : originalIndexA - originalIndexB;
            }
        });

        return {
            ...files,
            files: sortedFiles
        };
    };

    // Toggle sort order
    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            if (field === "date") {
                setSortOrder("desc");
            } else {
                setSortOrder("asc");
            }
        }
    };

    // Get sort icon
    const getSortIcon = (field) => {
        if (sortBy !== field) return "↕️";
        return sortOrder === "asc" ? "↑" : "↓";
    };

    // Get sort description
    const getSortDescription = () => {
        if (sortBy === "date") {
            return sortOrder === "desc" ? "Newest First" : "Oldest First";
        } else {
            return sortOrder === "asc" ? "A → Z" : "Z → A";
        }
    };

    // Get sorted files
    const sortedListFiles = listFiles ? sortFiles(listFiles) : null;

    useEffect(() => {
        getFiles(book_title);
    }, [book_title, updated]);

    // Stop audio when component unmounts or transcript closes
    useEffect(() => {
        return () => {
            stopAllAudio();
        };
    }, []);

    return (
        <div className="flex flex-col bg-white rounded-xl shadow-lg p-6 m-auto max-w-6xl">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <button
                    type="button"
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors"
                    onClick={() => successHandler(null)}
                    title="Back to titles"
                >
                    <span aria-hidden="true">←</span>
                    <span>Back to titles</span>
                </button>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-6 py-3 shadow-sm">
                    <p className="text-center text-lg font-semibold text-blue-800">
                        {book_title.author} : {book_title.title}
                    </p>
                </div>
                
                <div className="w-[132px]" aria-hidden="true"></div>
            </div>

            {/* Main Content */}
            <div className={`grid ${transcript ? "lg:grid-cols-2" : "grid-cols-1"} gap-6`}>
                {/* Left Panel - Upload and Processing */}
                <div className="space-y-6">
                    <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600 mb-2">Upload Audio Files</p>
                        <p className="text-gray-600">Generate transcriptions for your audio files</p>
                    </div>

                    <UploadFile book={book_title} successHandler={(state) => setUpdated(state)} />

                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-6">
                        {/* Model Selection */}
                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-5">
                            <p className="text-lg font-semibold text-blue-700 mb-3">
                                Select Transcription Model
                            </p>
                            <DropdownModel
                                list={models}
                                selectHandler={(event) => setModel(event.target.value)}
                                elvalue={model}
                            />
                            <p className="text-sm text-gray-600 mt-2">
                                Choose the appropriate model for {book_title.language} transcription
                            </p>
                        </div>

                        {/* Audio Files Section */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                                <p className="text-center text-xl font-semibold text-blue-600">
                                    Audio Files
                                </p>
                                
                                {sortedListFiles && sortedListFiles.files.length > 0 && (
                                    <div className="flex flex-wrap gap-2 items-center">
                                        <span className="text-sm font-medium text-gray-700">Sort by:</span>
                                        <button
                                            onClick={() => handleSort("date")}
                                            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                                sortBy === "date" 
                                                ? "bg-blue-500 text-white" 
                                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                            }`}
                                        >
                                            Upload Date {getSortIcon("date")}
                                        </button>
                                        <button
                                            onClick={() => handleSort("name")}
                                            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                                sortBy === "name" 
                                                ? "bg-blue-500 text-white" 
                                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                            }`}
                                        >
                                            File Name {getSortIcon("name")}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {sortedListFiles && sortedListFiles.files.length > 0 && (
                                <div className="text-center mb-4">
                                    <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                                        Sorted by {sortBy === "date" ? "upload date" : "file name"} - {getSortDescription()}
                                    </span>
                                </div>
                            )}
                            
                            <div className="space-y-4">
                                {sortedListFiles && sortedListFiles.files.map((file, i) => (
                                    <ProcessFile
                                        key={i}
                                        book={book_title}
                                        file={file}
                                        model_size={model}
                                        transcriptHandler={(state) => setTranscript(state)}
                                    />
                                ))}
                                
                                {(!sortedListFiles || sortedListFiles.files.length === 0) && (
                                    <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                                        <p className="text-gray-500">No audio files uploaded yet</p>
                                        <p className="text-sm text-gray-400 mt-1">
                                            Upload audio files to get started with transcription
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Panel - Transcript */}
                {transcript && (
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-blue-600">Transcript</h3>
                            <div className="flex items-center gap-2">
                                {/* Stop Audio Button */}
                                {isPlaying && (
                                    <button
                                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                                        onClick={stopAllAudio}
                                        title="Stop audio"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                )}
                                <button
                                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                                    onClick={handleCloseTranscript}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                            {transcript.transcript?.segments?.map((item, i) => (
                                <div
                                    key={i}
                                    className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                                        currentSegment === item
                                            ? 'bg-blue-100 border-blue-300'
                                            : 'bg-gray-50 border-gray-200 hover:bg-blue-50'
                                    }`}
                                >
                                    <div className="flex-1">
                                        <p className="text-gray-800 leading-relaxed">{item.text}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {Math.floor(item.start / 60)}:{(item.start % 60).toFixed(2)} - {Math.floor(item.end / 60)}:{(item.end % 60).toFixed(2)}
                                        </p>
                                    </div>
                                    <button
                                        className={`flex-shrink-0 p-2 rounded-full transition-colors shadow-sm ${
                                            currentSegment === item && isPlaying
                                                ? 'bg-red-500 hover:bg-red-600 text-white'
                                                : 'bg-blue-500 hover:bg-blue-600 text-white'
                                        }`}
                                        onClick={() => playAudioSegment(item)}
                                        title={currentSegment === item && isPlaying ? "Pause" : "Play this segment"}
                                    >
                                        <svg 
                                            xmlns="http://www.w3.org/2000/svg" 
                                            className="h-4 w-4" 
                                            viewBox="0 0 20 20" 
                                            fill="currentColor"
                                        >
                                            {currentSegment === item && isPlaying ? (
                                                // Pause icon
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                            ) : (
                                                // Play icon
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                            )}
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>

                        {(!transcript.transcript?.segments || transcript.transcript.segments.length === 0) && (
                            <div className="text-center py-8 text-gray-500">
                                No transcript segments available
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BookTitle;